import { Fragment } from 'react';
import { observer } from 'mobx-react';

import { URL_PRODUCT_PERMISSIONS_DOC } from '@/catalog/constants/urls';
import Alert from '@/common/components/Alert/Alert';
import Checkbox from '@/common/components/inputs/Checkbox/Checkbox';
import BaseGlobalAccessModal from '@/security/components/BaseGlobalAccessModal/BaseGlobalAccessModal';
import GlobalAccessLevelRadio from '@/security/components/BaseGlobalAccessModal/GlobalAccessLevelRadio/GlobalAccessLevelRadio';
import GlobalAccessModalFooter from '@/security/components/BaseGlobalAccessModal/GlobalAccessModalFooter/GlobalAccessModalFooter';
import {
  COLLECTION_ACCESS_CHANGE_TEXT,
  DRIVE_FOLDER_ACCESS_CHANGE_TEXT,
  DRIVE_PROJECT_ACCESS_CHANGE_TEXT,
  getAccessByType,
  GlobalAccessChangeText,
} from '@/security/constants/access';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { DriveGlobalAccessType } from '@/security/types/AccessTarget';

import { useDriveGlobalAccessModalStore } from '../DriveGlobalAccessModalStore';

import DataAccessColumn from './DataAccessColumn/DataAccessColumn';
import styles from './DriveGlobalAccessModalContent.module.css';

const ACCESS_CHANGE_TEXT: Record<DriveGlobalAccessType, GlobalAccessChangeText> = {
  driveProject: DRIVE_PROJECT_ACCESS_CHANGE_TEXT,
  driveFolder: DRIVE_FOLDER_ACCESS_CHANGE_TEXT,
  collection: COLLECTION_ACCESS_CHANGE_TEXT,
};

interface DriveGlobalAccessModalContentProps {
  type: DriveGlobalAccessType;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

function DriveGlobalAccessModalContent(props: DriveGlobalAccessModalContentProps) {
  const { type, isOpen, onClose, onSuccess } = props;

  const {
    access,
    accessDiff,
    dataAccessDiff,
    isDataAccessEditable,
    isInheritAccessEditable,
    shouldInheritAccess,
    setShouldInheritAccess,
    willRemovePublicAccess,
    isUpdating,
    reset,
    setAccess,
    updateAccessList,
  } = useDriveGlobalAccessModalStore();

  function onCloseHandler() {
    reset();
    onClose();
  }

  const onUpdateAccess = async () => {
    await updateAccessList();

    onCloseHandler();
    onSuccess?.();
  };

  return (
    <BaseGlobalAccessModal
      disableClose={isUpdating}
      onClose={onCloseHandler}
      isOpen={isOpen}
      title="Share Global Access"
      leftColumnTitle={isDataAccessEditable ? 'General' : null}
      leftColumn={getAccessByType(type).map((option) => (
        <GlobalAccessLevelRadio
          aria-label={option.title}
          access={option.id}
          key={option.id}
          checked={access === option.id}
          description={option.description}
          disabled={isUpdating}
          onChange={setAccess}
          title={option.title}
          type={type}
        />
      ))}
      rightColumnTitle={isDataAccessEditable ? 'Content Data' : null}
      rightColumn={isDataAccessEditable ? <DataAccessColumn /> : null}
      bottom={(
        <Fragment>
          <div className={styles.accessChangeInfo}>
            {isInheritAccessEditable && access === GlobalAccessLevel.Public && (
              <Checkbox
                aria-label="Inherit access"
                checked={shouldInheritAccess}
                disabled={isUpdating}
                label={ACCESS_CHANGE_TEXT[type].inherit}
                onChange={() => setShouldInheritAccess(!shouldInheritAccess)}
                type="checkbox"
              />
            )}

            {willRemovePublicAccess && (
              <Alert type="danger" rounded>
                {ACCESS_CHANGE_TEXT[type].revoke}
              </Alert>
            )}
          </div>

          <GlobalAccessModalFooter
            docHref={URL_PRODUCT_PERMISSIONS_DOC}
            isDisabled={isUpdating}
            isLoading={isUpdating}
            isSubmitDisabled={
              !(
                accessDiff ||
                dataAccessDiff ||
                (isInheritAccessEditable && access === GlobalAccessLevel.Public && shouldInheritAccess)
              )
            }
            onClose={onCloseHandler}
            onSubmit={onUpdateAccess}
          />
        </Fragment>
      )}
    />
  );
}

export default observer(DriveGlobalAccessModalContent);
